import { Link } from 'react-router-dom';
import { ArrowUpRight, Filter } from 'lucide-react';
import { formatAmount } from '@common/utils';
import { MockBooking } from '../../types/dashboard.types';
import { getStatusConfig, getTypeLabel } from '../../utils/dashboard.utils';
import { cn } from '../../lib/utils';


export function RecentTransactionsTable({
  filteredBookings,
  hasActiveFilters,
  resetFilters,
}: {
  filteredBookings: MockBooking[];
  hasActiveFilters: boolean;
  resetFilters: () => void;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          Transactions Récentes{' '}
          {hasActiveFilters && (
            <span className="text-primary text-xs bg-primary/10 px-2 py-0.5 rounded-full">Filtrées</span>
          )}
        </h3>
        <Link
          to="/bookings"
          className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
        >
          Voir tout
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-[#f8fafc] text-slate-500 border-b border-slate-200 font-medium whitespace-nowrap">
            <tr>
              <th className="px-5 py-3.5 pl-6">Référence</th>
              <th className="px-5 py-3.5">Client & Date</th>
              <th className="px-5 py-3.5">Espace</th>
              <th className="px-5 py-3.5">Statut</th>
              <th className="px-5 py-3.5 pr-6 text-right">Montant HT</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {filteredBookings.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <Filter className="w-8 h-8 opacity-20" />
                    <p>Aucune réservation ne correspond à vos filtres.</p>
                    <button onClick={resetFilters} className="mt-2 text-primary font-medium hover:underline">
                      Effacer les filtres
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredBookings.slice(0, 5).map((b) => {
                const status = getStatusConfig(b.status);
                const typeLabel = getTypeLabel(b.type);
                const TypeIcon = typeLabel.icon;

                return (
                  <tr key={b.id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-5 py-3.5 pl-6 align-middle">
                      <Link
                        to={`/bookings/${b.id}`}
                        className="font-semibold text-primary hover:underline flex items-center gap-1.5"
                      >
                        {b.ref}
                      </Link>
                    </td>
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex flex-col justify-center">
                        <span className="font-medium text-slate-900">{b.client}</span>
                        <span className="text-xs text-slate-400 mt-0.5">{b.date}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 align-middle">
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-white border border-slate-200 shadow-sm rounded-md text-slate-500 group-hover:border-slate-300 transition-colors">
                          <TypeIcon className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-slate-600 font-medium">{typeLabel.label}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 align-middle">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase border border-white/20',
                          status.bg,
                          status.text,
                        )}
                      >
                        <status.icon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 pr-6 font-bold text-right text-slate-900 align-middle">
                      {formatAmount(b.amount)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
